import { useQuery } from '@tanstack/react-query';
import { listAppInstances } from '@/features/apps/api';
import type { StatusFilter, TimeRange } from '../types';

const selectClass = 'text-sm w-auto min-w-[7.5rem] py-1.5 pl-2.5 pr-7 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900';

export function AppFilterSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const q = useQuery({
    queryKey: ['app-instances', 'agent-actions-filter'],
    queryFn: listAppInstances,
    staleTime: 30_000,
  });
  const apps = Array.isArray(q.data?.apps) ? q.data!.apps : [];

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={selectClass}
      aria-label="Filter by app"
    >
      <option value="">All apps</option>
      {apps.map((a) => (
        <option key={a.app_id} value={a.app_id}>
          {a.name}
        </option>
      ))}
    </select>
  );
}

export function TimeRangeSelect({
  value,
  onChange,
}: {
  value: TimeRange;
  onChange: (v: TimeRange) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as TimeRange)}
      className={selectClass}
      aria-label="Time range"
    >
      <option value="24h">Last 24h</option>
      <option value="7d">Last 7 days</option>
      <option value="30d">Last 30 days</option>
    </select>
  );
}

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'failures', label: 'Failures only' },
  { value: 'fixes', label: 'Fixes only' },
  { value: 'deploys', label: 'Deploys only' },
  { value: 'pending', label: 'Pending review' },
  { value: 'agent_calls', label: 'Agent calls' },
  { value: 'data_sync', label: 'Data sync' },
  { value: 'code_commits', label: 'Code commits' },
];

export function StatusFilterSelect({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as StatusFilter)}
      className={selectClass}
      aria-label="Status filter"
    >
      {STATUS_OPTIONS.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}
